import React from "react";
import { TextField, Chip, Box, InputAdornment, Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import "../../../styles/components/TextInput.scss";

export interface CustomChipInputProps {
  placeHolder?: string;
  customError?: string;
  infoMessage?: string;
  isInfoMessage?: boolean;
  chipOptions: string[]; // Controlled by Settings
  currentValue: string;
  setCurrentValue: (value: string) => void;
  onAddChip: () => void;
  onRemoveChip: (value: string) => void;
  className?: string;
}

const CustomChipInput: React.FC<CustomChipInputProps> = ({
  placeHolder = "general.placeholders.generalInputPlaceHolder",
  customError = "general.errors.invalidInput",
  infoMessage,
  isInfoMessage = false,
  chipOptions,
  currentValue,
  setCurrentValue,
  onAddChip,
  onRemoveChip,
  className,
}) => {
  const { t } = useTranslation();
  const hasValue = currentValue.trim().length > 0;

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && hasValue) {
      e.preventDefault();
      onAddChip();
    }
  };

  return (
    <div className={`custom-chip-input-container ${className || ""}`}>
      <Box>
        <Box sx={{ display: "flex", flexDirection: "column", }}>
          <TextField
            variant="outlined"
            label={placeHolder ? t(placeHolder) : "Email"}
            value={currentValue}
            onChange={(e) => setCurrentValue(e.target.value)}
            fullWidth
            onKeyDown={handleKeyDown}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Button
                    onClick={onAddChip}
                    size="small"
                    variant="text"
                    color="primary"
                    disableElevation
                    sx={{
                      textTransform: "none",
                      color: "#2F75DA",
                      width: "auto",
                      justifyContent: "center",
                      padding: 0,
                      minWidth: "auto",
                      display: "flex",
                      alignItems: "center",
                      gap: "9px",
                      "&:hover": {
                        backgroundColor: "transparent",
                        color: "#2F75DA",
                      }
                    }}
                  >
                    {hasValue ? t('general.buttons.save') : (
                      <>
                        <svg width="11" height="11" viewBox="0 0 11 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M5.50016 2.2915V8.70817M2.29183 5.49984H8.7085" stroke="#2F75DA" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        {t('settings.addEmail')}
                      </>
                    )}
                  </Button>
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                "&:hover fieldset": { borderColor: "#1A5DBA" },
                "&.Mui-focused fieldset": { borderColor: "#0047AB" },
              },
              "& .MuiInputLabel-root": { color: "#2F75DA" },
              "& .MuiInputLabel-root.Mui-focused": { color: "#0047AB" },
            }}
            className={className ? `${className}-field` : ""}
          />
          {isInfoMessage && infoMessage && <div className="info-input">{t(infoMessage)}</div>}
        </Box>

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 ,marginTop: "16px" }}>
          {chipOptions.map((email, index) => (
            <Chip
              key={index}
              label={email}
              onDelete={() => onRemoveChip(email)}
              color="primary"
              variant="outlined"
            />
          ))}
        </Box>
      </Box>
    </div>
  );
};

export default CustomChipInput;
